import { inject, Injectable, InjectionToken, signal } from '@angular/core';
import { IdentityServiceBase } from '@seahorse/auth';
import {
  PreferenceDataService,
  PreferenceFieldType,
  PreferenceModel,
} from '@seahorse/domain';

export type ShTableDensity = 'comfortable' | 'compact' | 'condensed';

/** The density of a table that neither forces one nor has a user choice. */
export const SH_TABLE_DEFAULT_DENSITY: ShTableDensity = 'condensed';

export const SH_TABLE_DENSITIES: ShTableDensity[] = [
  'comfortable',
  'compact',
  'condensed',
];

/**
 * Where an `sh-table` keeps per-table user choices. The table only knows this
 * interface; the default implementation stores them in the user preferences.
 * `density` is read inside `computed()`, so an implementation should back it
 * with a signal to make the table follow `setDensity` immediately.
 */
export interface ShTablePreferences {
  density(tableId: string): ShTableDensity | undefined;
  setDensity(tableId: string, density: ShTableDensity): void;
}

export const TABLE_PREFERENCE_CATEGORY = 'table';

function isDensity(value: unknown): value is ShTableDensity {
  return SH_TABLE_DENSITIES.includes(value as ShTableDensity);
}

/** The stored value is JSON (`{"density":"condensed"}`) so more can be added later. */
function parseValue(value: unknown): Record<string, unknown> {
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
}

/**
 * The user's table preferences: category `table`, name = the table id, a JSON
 * string value. Preferences are preloaded into `IdentityServiceBase` at
 * startup; a change updates a local cache first (so the table reacts at once),
 * then saves, then mirrors the saved preference into the identity service. A
 * failed save is ignored: the choice then lasts until the page reloads.
 */
@Injectable({ providedIn: 'root' })
export class UserTablePreferencesService implements ShTablePreferences {
  private readonly _identity = inject(IdentityServiceBase);
  private readonly _preferences = inject(PreferenceDataService);
  private readonly _cache = signal<Record<string, ShTableDensity>>({});

  density(tableId: string): ShTableDensity | undefined {
    const cached = this._cache()[tableId];
    if (cached) return cached;
    const density = parseValue(this._find(tableId)?.fieldValue)['density'];
    return isDensity(density) ? density : undefined;
  }

  setDensity(tableId: string, density: ShTableDensity): void {
    this._cache.update((cache) => ({ ...cache, [tableId]: density }));

    // Only the fields the API needs (it matches an existing preference by
    // name and category). A `new PreferenceModel()` carries `id: null` and
    // `isCompanyPreference: null`, which the API can't bind to its
    // non-nullable fields: it then gets no model at all and rejects the save.
    const existing = this._find(tableId);
    const preference = {
      ...(existing?.id != null ? { id: existing.id } : {}),
      name: tableId,
      isCompanyPreference: false,
      category: TABLE_PREFERENCE_CATEGORY,
      fieldType: PreferenceFieldType.StringField,
      fieldValue: JSON.stringify({
        ...parseValue(existing?.fieldValue),
        density,
      }),
    } as PreferenceModel;

    this._preferences.save(preference).subscribe({
      next: (response) => {
        if (response?.hasResult) {
          this._identity.setPreferences([response.result]);
        }
      },
      error: () => undefined,
    });
  }

  private _find(tableId: string): PreferenceModel | undefined {
    return this._identity
      .getPreferences()
      ?.find(
        (p) => p.category === TABLE_PREFERENCE_CATEGORY && p.name === tableId
      );
  }
}

export const SH_TABLE_PREFERENCES = new InjectionToken<ShTablePreferences>(
  'SH_TABLE_PREFERENCES',
  {
    providedIn: 'root',
    factory: () =>
      inject<UserTablePreferencesService>(UserTablePreferencesService),
  }
);
